import { ArrowRight } from "lucide-react";
import { LINKS } from "@/lib/brand";

const OUTPUTS = [
  {
    n: "01",
    title: "Website & Conversion Experience",
    body:
      "Messaging, structure, user journey and lead capture opportunities.",
  },
  {
    n: "02",
    title: "SEO & AI-Search Visibility",
    body:
      "Content structure and discoverability opportunities for search and AI-driven discovery.",
  },
  {
    n: "03",
    title: "Automation Opportunities",
    body:
      "Processes, lead handling and repetitive work that may be streamlined.",
  },
  {
    n: "04",
    title: "Voice Workflow Potential",
    body:
      "Where AI voice agents could support calls, qualification or follow-up.",
  },
  {
    n: "05",
    title: "Product & Software Opportunities",
    body:
      "Applications, portals or vertical SaaS concepts worth evaluating.",
  },
];

export default function AuditIntro() {
  return (
    <section
      id="audit"
      className="section"
      data-testid="audit-intro"
      style={{
        borderTop: "1px solid var(--line)",
        background:
          "radial-gradient(80% 60% at 20% 0%, rgba(30,144,255,0.05), rgba(0,0,0,0) 60%)",
      }}
    >
      <div className="container-edge audit-intro-shell">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.05fr)",
            gap: "clamp(40px, 6vw, 96px)",
            alignItems: "start",
            marginBottom: 56,
          }}
          className="grid-md-stack"
        >
          <div>
            <div className="eyebrow reveal" data-testid="audit-intro-eyebrow">
              Free AI Audit
            </div>
            <h2
              className="font-display reveal reveal-delay-1"
              style={{
                fontSize: "clamp(34px, 4.4vw, 60px)",
                lineHeight: 1.05,
                margin: "16px 0 0",
                color: "#fff",
                letterSpacing: "-0.025em",
                fontWeight: 500,
              }}
              data-word-reveal
              data-testid="audit-intro-headline"
            >
              Your website is only one layer of the opportunity.
            </h2>
          </div>

          <div>
            <p
              className="reveal reveal-delay-2"
              style={{
                color: "var(--ink-soft)",
                fontSize: 16,
                lineHeight: 1.65,
                maxWidth: 520,
              }}
              data-testid="audit-intro-body"
            >
              Add your website if you have one and answer a few questions.
              ForgeLayer can later assess publicly visible digital signals
              alongside your business priorities to identify where AI,
              automation, voice workflows and stronger software systems could
              create the most value.
            </p>
            <a
              href="#audit-form"
              className="btn-primary reveal reveal-delay-3"
              style={{ marginTop: 24 }}
              data-testid="audit-intro-cta"
            >
              Start My Free Audit
              <span className="btn-arrow" aria-hidden="true">
                <ArrowRight size={13} strokeWidth={2.2} />
              </span>
            </a>
          </div>
        </div>


        {/* 5-part output architecture */}
        <ol
          style={{
            listStyle: "none",
            padding: 0,
            margin: 0,
            display: "grid",
            gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
            gap: 0,
            border: "1px solid var(--line)",
            borderRadius: 16,
            overflow: "hidden",
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.015), rgba(255,255,255,0))",
          }}
          className="audit-output-grid"
          aria-label="Audit output sections"
        >
          {OUTPUTS.map((o, i) => (
            <li
              key={o.n}
              className={`reveal reveal-delay-${(i % 4) + 1}`}
              style={{
                padding: "28px 24px 30px",
                borderRight:
                  i < OUTPUTS.length - 1 ? "1px solid var(--line)" : "none",
                display: "flex",
                flexDirection: "column",
                gap: 12,
              }}
              data-testid={`audit-output-${i}`}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span
                  className="font-mono"
                  style={{
                    color: "var(--accent-soft)",
                    fontSize: 11,
                    letterSpacing: "0.18em",
                  }}
                >
                  {o.n}
                </span>
                <span
                  aria-hidden="true"
                  style={{
                    width: 5,
                    height: 5,
                    borderRadius: 999,
                    background: "var(--accent)",
                    boxShadow: "0 0 10px var(--accent-glow)",
                  }}
                />
              </div>
              <h3
                className="font-display"
                style={{
                  color: "#fff",
                  fontSize: 16,
                  fontWeight: 500,
                  letterSpacing: "-0.015em",
                  margin: 0,
                  lineHeight: 1.3,
                }}
              >
                {o.title}
              </h3>
              <p
                style={{
                  color: "var(--silver)",
                  fontSize: 13,
                  lineHeight: 1.55,
                  margin: 0,
                }}
              >
                {o.body}
              </p>
            </li>
          ))}
        </ol>
      </div>

      <style>{`
        @media (max-width: 1080px) {
          .audit-output-grid { grid-template-columns: repeat(3, 1fr) !important; }
          .audit-output-grid li { border-right: 1px solid var(--line) !important; border-bottom: 1px solid var(--line); }
          .audit-output-grid li:nth-child(3n) { border-right: none !important; }
          .audit-output-grid li:nth-last-child(-n+2) { border-bottom: none; }
        }
        @media (max-width: 640px) {
          .audit-output-grid { grid-template-columns: 1fr !important; }
          .audit-output-grid li { border-right: none !important; border-bottom: 1px solid var(--line) !important; }
          .audit-output-grid li:last-child { border-bottom: none !important; }
        }
      `}</style>
      {/* Pseudo-link to scroll target marker for the funnel below.
          Used because the form starts immediately below this intro. */}
      <div style={{ height: 0 }} aria-hidden="true" />
    </section>
  );
}
